import React, { useEffect, useRef, useState } from 'react'
import {
  Bold,
  Italic,
  List,
  Link as LinkIcon,
  RemoveFormatting,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface RichTextEditorProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
}

export const RichTextEditor = ({
  value,
  onChange,
  placeholder,
  className,
}: RichTextEditorProps) => {
  const editorRef = useRef<HTMLDivElement>(null)
  const [isFocused, setIsFocused] = useState(false)

  // Initialize content
  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      // Only update if content is different to avoid cursor jumping
      if (editorRef.current.innerHTML === '' && value) {
        editorRef.current.innerHTML = value
      } else if (value === '' && editorRef.current.innerHTML === '<br>') {
        // Do nothing, browser often leaves a <br>
      }
    }
  }, []) // On mount

  // Sync value when it changes externally, but be careful about cursor
  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      // If the difference is just empty paragraphs or breaks, ignore
      if (
        value === '' &&
        (editorRef.current.innerHTML === '<br>' ||
          editorRef.current.innerText.trim() === '')
      ) {
        return
      }
      // This is a naive implementation; complex syncing needs a real library.
      // For this simple task, we assume external updates are rare (mostly initial load).
      if (!isFocused) {
        editorRef.current.innerHTML = value
      }
    }
  }, [value, isFocused])

  const execCommand = (
    command: string,
    value: string | undefined = undefined,
  ) => {
    document.execCommand(command, false, value)
    handleInput()
    editorRef.current?.focus()
  }

  const handleInput = () => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML
      onChange(html === '<br>' ? '' : html)
    }
  }

  const addLink = () => {
    const url = prompt('Enter URL:')
    if (url) {
      execCommand('createLink', url)
    }
  }

  return (
    <div
      className={cn(
        'border rounded-md overflow-hidden bg-background ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
        className,
      )}
    >
      <div className="flex items-center gap-1 p-1 border-b bg-muted/20">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => execCommand('bold')}
          className="h-8 w-8 p-0"
          title="Bold"
        >
          <Bold className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => execCommand('italic')}
          className="h-8 w-8 p-0"
          title="Italic"
        >
          <Italic className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => execCommand('insertUnorderedList')}
          className="h-8 w-8 p-0"
          title="Bullet List"
        >
          <List className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={addLink}
          className="h-8 w-8 p-0"
          title="Link"
        >
          <LinkIcon className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => execCommand('removeFormat')}
          className="h-8 w-8 p-0"
          title="Clear Formatting"
        >
          <RemoveFormatting className="h-4 w-4" />
        </Button>
      </div>
      <div
        ref={editorRef}
        className="p-3 min-h-[120px] outline-none prose prose-sm max-w-none prose-slate"
        contentEditable
        onInput={handleInput}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false)
          handleInput()
        }}
        data-placeholder={placeholder}
        style={{
          whiteSpace: 'pre-wrap',
        }}
      />
      <style>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
      `}</style>
    </div>
  )
}
